import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import type { DropResult } from '@hello-pangea/dnd';
import { useTranslation } from 'react-i18next';
import PageSection from '../../../components/layout/PageSection';
import ManagementSearchToolbar from './ManagementSearchToolbar';
import type { WarehouseCategory } from '../types';

interface CategoryTreePanelProps {
  categories: WarehouseCategory[];
  totalCount: number | null;
  rootCategories: WarehouseCategory[];
  childrenByParent: Record<string, WarehouseCategory[]>;
  orphanedChildren: WarehouseCategory[];
  canManage: boolean;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onClearSearch: () => void;
  onScroll: (event: React.UIEvent<HTMLDivElement>) => void;
  onDragEnd: (result: DropResult) => void;
  onEdit: (category: WarehouseCategory) => void;
  onDelete: (categoryId: number | string) => void;
  isFetchingNextPage: boolean;
}

interface CategoryRowProps {
  category: WarehouseCategory;
  canManage: boolean;
  onEdit: (category: WarehouseCategory) => void;
  onDelete: (categoryId: number | string) => void;
}

const CategoryActions = ({ category, canManage, onEdit, onDelete }: CategoryRowProps) => {
  if (!canManage) return null;

  return (
    <div className="btn-group">
      <button className="btn btn-sm btn-outline-primary" onClick={() => onEdit(category)} title="Edit category">
        <i className="ti ti-pencil" aria-hidden="true" />
      </button>
      <button className="btn btn-sm btn-outline-danger" onClick={() => onDelete(category.id)} title="Delete category">
        <i className="ti ti-trash" aria-hidden="true" />
      </button>
    </div>
  );
};

const CategoryTreePanel = ({
  categories,
  totalCount,
  rootCategories,
  childrenByParent,
  orphanedChildren,
  canManage,
  searchQuery,
  onSearchChange,
  onClearSearch,
  onScroll,
  onDragEnd,
  onEdit,
  onDelete,
  isFetchingNextPage,
}: CategoryTreePanelProps) => {
  const { t } = useTranslation();

  return (
    <PageSection
      title={t('warehouses.categories.title')}
      description={t('warehouses.categories.sectionDescription', 'Search categories, adjust hierarchy and reorder priority blocks from one surface.')}
      bodyClassName="p-0"
    >
      <DragDropContext onDragEnd={onDragEnd}>
        <ManagementSearchToolbar
          searchQuery={searchQuery}
          onSearchChange={onSearchChange}
          totalCount={totalCount}
          visibleCount={categories.length}
          entityLabel={t('warehouses.categories.title').toLowerCase()}
          placeholder={`${t('common.search')}...`}
          className="p-3 border-bottom"
        />
        <div
          className="card-body p-0"
          onScroll={onScroll}
          style={{ minHeight: '200px', maxHeight: '70vh', overflowY: 'auto', position: 'relative' }}
        >
          <Droppable droppableId="children-null">
            {(unassignedProvided) => (
              <div ref={unassignedProvided.innerRef} {...unassignedProvided.droppableProps} style={{ height: 0, overflow: 'hidden' }}>
                {unassignedProvided.placeholder}
              </div>
            )}
          </Droppable>

          <Droppable droppableId="root-groups" type="GROUP" isCombineEnabled>
            {(groupProvided) => (
              <div ref={groupProvided.innerRef} {...groupProvided.droppableProps} style={{ minHeight: 40 }}>
                {rootCategories.map((root, rootIndex) => {
                  const children = childrenByParent[String(root.id)] || [];
                  return (
                    <Draggable key={`group-${root.id}`} draggableId={`group-${root.id}`} index={rootIndex}>
                      {(groupDrag, groupSnap) => (
                        <div
                          ref={groupDrag.innerRef}
                          {...groupDrag.draggableProps}
                          style={{
                            ...groupDrag.draggableProps.style,
                            boxShadow: groupSnap.isDragging ? '0 4px 12px rgba(0,0,0,0.15)' : 'none',
                            outline: groupSnap.combineTargetFor ? '2px solid var(--tblr-primary)' : 'none',
                            outlineOffset: -2,
                          }}
                        >
                          <div
                            className="border-bottom d-flex align-items-center"
                            style={{
                              backgroundColor: 'var(--tblr-bg-surface)',
                              borderLeft: groupSnap.isDragging ? '4px solid var(--tblr-primary)' : '4px solid transparent',
                              padding: '0.75rem 1rem',
                            }}
                          >
                            <div {...groupDrag.dragHandleProps} className="me-3 text-muted d-flex align-items-center" style={{ cursor: groupSnap.isDragging ? 'grabbing' : 'grab' }}>
                              <i className="ti ti-grip-vertical" aria-hidden="true" />
                            </div>
                            <div className="me-3">
                              <span className="badge">{root.priority || 0}</span>
                            </div>
                            <div
                              className="me-3 flex-shrink-0"
                              style={{
                                width: 28,
                                height: 28,
                                backgroundColor: root.color || '#cccccc',
                                borderRadius: '4px',
                                border: '2px solid rgba(0,0,0,0.1)',
                              }}
                              title={root.color || undefined}
                            />
                            <div className="flex-grow-1 min-w-0">
                              <div className="fw-semibold text-truncate">
                                {root.name}
                                {children.length > 0 && (
                                  <span className="badge badge-outline text-muted ms-2" style={{ fontSize: '0.65rem', fontWeight: 400 }}>
                                    {children.length}
                                  </span>
                                )}
                              </div>
                              <div className="text-muted small text-truncate">{root.description || ''}</div>
                            </div>
                            <div className="ms-3 flex-shrink-0">
                              <CategoryActions category={root} canManage={canManage} onEdit={onEdit} onDelete={onDelete} />
                            </div>
                          </div>

                          <Droppable droppableId={`children-${root.id}`}>
                            {(childProvided, childDropSnap) => (
                              <div
                                ref={childProvided.innerRef}
                                {...childProvided.droppableProps}
                                style={{
                                  minHeight: childDropSnap.isDraggingOver ? 56 : (children.length === 0 ? 0 : undefined),
                                  backgroundColor: childDropSnap.isDraggingOver ? 'rgba(var(--tblr-primary-rgb), 0.08)' : undefined,
                                  border: childDropSnap.isDraggingOver && children.length === 0 ? '2px dashed var(--tblr-primary)' : undefined,
                                  borderRadius: childDropSnap.isDraggingOver && children.length === 0 ? 4 : undefined,
                                  margin: childDropSnap.isDraggingOver && children.length === 0 ? '4px 1rem 4px 3rem' : undefined,
                                  transition: 'background-color 0.15s, min-height 0.15s',
                                }}
                              >
                                {childDropSnap.isDraggingOver && children.length === 0 && (
                                  <div className="text-muted text-center d-flex align-items-center justify-content-center" style={{ fontSize: '0.75rem', height: 56, pointerEvents: 'none' }}>
                                    {t('warehouses.categories.dropHereActive') || '↓ Drop to add child'}
                                  </div>
                                )}
                                {children.map((child, childIndex) => (
                                  <Draggable key={`item-${child.id}`} draggableId={`item-${child.id}`} index={childIndex}>
                                    {(childDrag, childSnap) => (
                                      <div
                                        ref={childDrag.innerRef}
                                        {...childDrag.draggableProps}
                                        className="border-bottom d-flex align-items-center"
                                        style={{
                                          ...childDrag.draggableProps.style,
                                          backgroundColor: childSnap.isDragging ? 'var(--tblr-bg-surface)' : 'var(--tblr-bg-surface-secondary)',
                                          borderLeft: childSnap.isDragging ? '4px solid var(--tblr-primary)' : `4px solid ${root.color || 'var(--tblr-border-color)'}`,
                                          boxShadow: childSnap.isDragging ? '0 4px 12px rgba(0,0,0,0.15)' : 'none',
                                          padding: '0.5rem 1rem 0.5rem 2.5rem',
                                        }}
                                      >
                                        <div {...childDrag.dragHandleProps} className="me-3 text-muted d-flex align-items-center" style={{ cursor: childSnap.isDragging ? 'grabbing' : 'grab' }}>
                                          <i className="ti ti-grip-vertical" aria-hidden="true" />
                                        </div>
                                        <div className="me-3">
                                          <span className="badge">{child.priority || 0}</span>
                                        </div>
                                        <div
                                          className="me-3 flex-shrink-0"
                                          style={{
                                            width: 20,
                                            height: 20,
                                            backgroundColor: child.color || '#cccccc',
                                            borderRadius: '4px',
                                            border: '2px solid rgba(0,0,0,0.1)',
                                          }}
                                          title={child.color || undefined}
                                        />
                                        <div className="flex-grow-1 min-w-0">
                                          <div className="fw-semibold text-truncate small">{child.name}</div>
                                          <div className="text-muted small text-truncate">{child.description || ''}</div>
                                        </div>
                                        <div className="ms-3 flex-shrink-0">
                                          <CategoryActions category={child} canManage={canManage} onEdit={onEdit} onDelete={onDelete} />
                                        </div>
                                      </div>
                                    )}
                                  </Draggable>
                                ))}
                                {childProvided.placeholder}
                              </div>
                            )}
                          </Droppable>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {groupProvided.placeholder}
              </div>
            )}
          </Droppable>

          {orphanedChildren.length > 0 && (
            <div style={{ borderTop: '2px dashed var(--tblr-border-color-muted)' }}>
              <div className="px-3 py-1 text-muted" style={{ fontSize: '0.7rem' }}>
                {t('warehouses.categories.orphaned') || 'Orphaned (parent not on this page)'}
              </div>
              {orphanedChildren.map((child) => (
                <div
                  key={child.id}
                  className="border-bottom d-flex align-items-center"
                  style={{
                    backgroundColor: 'var(--tblr-bg-surface-secondary)',
                    borderLeft: '4px solid var(--tblr-border-color)',
                    padding: '0.5rem 1rem 0.5rem 2.5rem',
                    opacity: 0.7,
                  }}
                >
                  <div className="me-3">
                    <span className="badge">{child.priority || 0}</span>
                  </div>
                  <div
                    className="me-3 flex-shrink-0"
                    style={{ width: 20, height: 20, backgroundColor: child.color || '#cccccc', borderRadius: '4px', border: '2px solid rgba(0,0,0,0.1)' }}
                    title={child.color || undefined}
                  />
                  <div className="flex-grow-1 min-w-0">
                    <div className="fw-semibold text-truncate small">{child.name}</div>
                    <div className="text-muted small text-truncate">{child.description || ''}</div>
                  </div>
                  <div className="ms-3 flex-shrink-0">
                    <CategoryActions category={child} canManage={canManage} onEdit={onEdit} onDelete={onDelete} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {isFetchingNextPage && (
            <div className="text-center py-3 text-muted border-top">
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              {t('common.loading')}
            </div>
          )}
        </div>
      </DragDropContext>
    </PageSection>
  );
};

export default CategoryTreePanel;
